<template>
  <el-cascader
    v-model="innerModelValue"
    v-bind="cascaderAttrs"
    :options="options"
    :props="cascaderProps"
    @change="handleChange"
  />
</template>

<script lang="ts" setup>
import { computed, ref, watch } from '@zto/zpage'
import { useFormItem } from '@zto/zpage-ui-element'
import PCACodeJson from '../../../../public/templates/pca-code.json'

const props = withDefaults(
  defineProps<{
    model: Record<string, any>
    prop: string
    multiple?: boolean
    /** 为 true 时允许只选省 / 省+市 / 省市区任意层级；默认 false 保持原有三联必选 */
    allowPartial?: boolean
    propsConfig?: Record<string, any>
    keyConfig?: {
      provinceCode?: string
      province?: string
      cityCode?: string
      city?: string
      districtCode?: string
      district?: string
    }
  }>(),
  {
    multiple: false,
    allowPartial: false,
    keyConfig: () => ({
      provinceCode: 'provinceCode',
      province: 'province',
      cityCode: 'cityCode',
      city: 'city',
      districtCode: 'districtCode',
      district: 'district'
    })
  }
)

const options = PCACodeJson

// 获取配置的 key，提供默认值
const fieldKeys = computed(() => {
  const defaultKeys = {
    provinceCode: 'provinceCode',
    province: 'province',
    cityCode: 'cityCode',
    city: 'city',
    districtCode: 'districtCode',
    district: 'district'
  }
  return { ...defaultKeys, ...props.keyConfig }
})

const cascaderProps = computed(() => {
  return {
    multiple: props.multiple === true,
    value: 'code',
    label: 'name',
    children: 'children',
    ...(props.allowPartial ? { checkStrictly: true } : {}),
    ...props.propsConfig
  }
})

/** 从区域对象提取级联 code 路径 */
function getCodesFromAreaItem(item: Record<string, any>): string[] {
  const codes = [
    item[fieldKeys.value.provinceCode],
    item[fieldKeys.value.cityCode],
    item[fieldKeys.value.districtCode]
  ]
  return props.allowPartial ? codes.filter(Boolean) : codes
}

// 使用 useFormItem hook，它会自动处理表单验证的清除
const { handleChange: formItemHandleChange, innerAttrs } = useFormItem(props, {
  customeChangeEvent: true // 自定义 change 事件
})

const cascaderAttrs = computed(() => {
  return {
    collapseTags: true,
    clearable: true,
    filterable: true,
    separator: '-',
    ...innerAttrs.value
  }
})

// 内部值，用于 v-model
const innerModelValue = ref<any[]>([])

// 监听外部值变化
watch(
  () => props.model[props.prop],
  (newVal) => {
    if (!newVal || (Array.isArray(newVal) && newVal.length === 0)) {
      innerModelValue.value = []
      return
    }

    if (Array.isArray(newVal) && newVal.length > 0) {
      // 如果已经是格式化后的数据（对象数组），提取 code 数组
      if (newVal[0] && typeof newVal[0] === 'object' && newVal[0][fieldKeys.value.provinceCode]) {
        if (props.multiple === true) {
          // 多选模式：转换为二维数组
          innerModelValue.value = newVal.map((item: any) => getCodesFromAreaItem(item))
        } else {
          // 单选模式：转换为一维数组（取第一个）
          innerModelValue.value = getCodesFromAreaItem(newVal[0])
        }
      } else {
        // 如果已经是 code 数组格式
        // 单选模式：一维数组 ['110000', '110100', '110101']
        // 多选模式：二维数组 [['110000', '110100', '110101'], ...]
        if (props.multiple === true) {
          // 多选模式：确保是二维数组
          if (newVal.length > 0 && Array.isArray(newVal[0])) {
            innerModelValue.value = newVal
          } else if (
            (props.allowPartial ? newVal.length >= 1 : newVal.length === 3) &&
            typeof newVal[0] === 'string'
          ) {
            // 如果是一维数组，转换为二维数组
            innerModelValue.value = [newVal]
          } else {
            innerModelValue.value = newVal
          }
        } else {
          // 单选模式：确保是一维数组
          if (newVal.length > 0 && Array.isArray(newVal[0])) {
            // 如果是二维数组，取第一个
            innerModelValue.value = newVal[0] || []
          } else {
            innerModelValue.value = newVal
          }
        }
      }
    } else {
      innerModelValue.value = []
    }
  },
  { immediate: true }
)

// 根据 code 查找省市区信息
function findAreaByCode(codes: string[]): Record<string, any> | null {
  if (!codes?.length) return null

  const [provinceCode, cityCode, districtCode] = codes

  // 查找省份
  const province = options.find((item: any) => item.code === provinceCode)
  if (!province) return null

  // 原逻辑：必须选满省市区三级
  if (!props.allowPartial) {
    if (codes.length < 3) return null

    const city = province.children?.find((item: any) => item.code === cityCode)
    if (!city) return null

    const district = city.children?.find((item: any) => item.code === districtCode)
    if (!district) return null

    return {
      [fieldKeys.value.provinceCode]: province.code,
      [fieldKeys.value.province]: province.name,
      [fieldKeys.value.cityCode]: city.code,
      [fieldKeys.value.city]: city.name,
      [fieldKeys.value.districtCode]: district.code,
      [fieldKeys.value.district]: district.name
    }
  }

  const result: Record<string, any> = {
    [fieldKeys.value.provinceCode]: province.code,
    [fieldKeys.value.province]: province.name,
    [fieldKeys.value.cityCode]: '',
    [fieldKeys.value.city]: '',
    [fieldKeys.value.districtCode]: '',
    [fieldKeys.value.district]: ''
  }

  if (codes.length === 1) return result

  const city = province.children?.find((item: any) => item.code === cityCode)
  if (!city) return null
  result[fieldKeys.value.cityCode] = city.code
  result[fieldKeys.value.city] = city.name

  if (codes.length === 2) return result

  const district = city.children?.find((item: any) => item.code === districtCode)
  if (!district) return null
  result[fieldKeys.value.districtCode] = district.code
  result[fieldKeys.value.district] = district.name

  return result
}

// 处理 change 事件
function handleChange(value: any) {
  if (!value || (Array.isArray(value) && value.length === 0)) {
    props.model[props.prop] = []
    formItemHandleChange([])
    return
  }

  // 如果是多选模式，value 是二维数组
  if (props.multiple === true && Array.isArray(value) && value.length > 0) {
    const formattedData = value
      .map((codes: string[]) => findAreaByCode(codes))
      .filter((item: any) => item !== null)

    props.model[props.prop] = formattedData
    formItemHandleChange(formattedData)
  } else if (Array.isArray(value) && value.length > 0) {
    // 单选模式，value 是一维数组
    if (!props.allowPartial && value.length !== 3) {
      props.model[props.prop] = []
      formItemHandleChange([])
      return
    }
    const formattedData = findAreaByCode(value)
    props.model[props.prop] = formattedData ? [formattedData] : []
    formItemHandleChange(formattedData ? [formattedData] : [])
  } else {
    props.model[props.prop] = []
    formItemHandleChange([])
  }
}
</script>

<style lang="scss" scoped></style>
